import React, { useState } from 'react';
import { 
  X, 
  Plus, 
  Minus, 
  Trash2, 
  QrCode, 
  Receipt as ReceiptIcon, 
  CheckCircle2, 
  Zap, 
  ShoppingBag, 
  DollarSign, 
  Smartphone,
  Beer,
  Scissors,
  Check,
  RotateCcw
} from 'lucide-react';
import { Appointment, PosRetailProduct } from '../../types';
import { formatCurrencyBRL } from '../../../../utils/formatUtils';
import { generateDynamicPixPayload } from '../../utils/pixUtils';

interface BarberQuickPosModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment?: Appointment | null;
  providerName: string;
  providerPixKey?: string;
  providerCity?: string;
  onCompleteWithReceipt?: (comandaData: {
    clientName: string;
    clientPhone?: string;
    items: Array<{ name: string; price: number; quantity: number; isProduct?: boolean }>;
    totalAmount: number;
    paymentMethod: 'pix' | 'cartao_credito' | 'cartao_debito' | 'dinheiro';
  }) => void;
}

const DEFAULT_POS_PRODUCTS: PosRetailProduct[] = [
  { id: 'p_pomada_matte', name: 'Pomada Efeito Matte (150g)', category: 'pomadas', price: 40, stockQuantity: 14, iconName: '🧴' },
  { id: 'p_oleo_barba', name: 'Óleo Hidratante Barba (30ml)', category: 'barba', price: 35, stockQuantity: 8, iconName: '🧔' },
  { id: 'p_balm_barba', name: 'Balm Refrescante Barba', category: 'barba', price: 35, stockQuantity: 6, iconName: '⚡' },
  { id: 'p_shampoo', name: 'Shampoo Anticaspa Mentol', category: 'cuidados', price: 38, stockQuantity: 10, iconName: '🚿' },
  { id: 'p_cerveja', name: 'Cerveja Long Neck / Heineken', category: 'bebidas', price: 12, stockQuantity: 24, iconName: '🍺' },
  { id: 'p_energetico', name: 'Energético / Refrigerante', category: 'bebidas', price: 8, stockQuantity: 18, iconName: '⚡' }
];

const QUICK_SERVICE_ADDONS = [
  { id: 'add_sobrancelha', name: 'Sobrancelha na Navalha', price: 20, icon: '✂️' },
  { id: 'add_lavagem', name: 'Lavagem Especial + Massagem', price: 25, icon: '💆‍♂️' },
  { id: 'add_pigmentacao', name: 'Pigmentação Express', price: 35, icon: '🎨' },
  { id: 'add_depilacao', name: 'Depilação Cera (Nariz/Ouvido)', price: 25, icon: '🔥' }
];

export default function BarberQuickPosModal({
  isOpen,
  onClose,
  appointment,
  providerName,
  providerPixKey = '',
  providerCity = 'São Paulo',
  onCompleteWithReceipt
}: BarberQuickPosModalProps) {
  if (!isOpen) return null;

  const clientName = appointment?.clientName || 'Cliente Balcão';
  const clientPhone = appointment?.clientPhone || '';
  const initialServiceName = appointment?.serviceName || 'Corte Masculino Fade';
  const initialServicePrice = appointment?.price || 45;

  const [comandaItems, setComandaItems] = useState<Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    isProduct: boolean;
    canceled?: boolean;
  }>>([
    { id: 'base_service', name: initialServiceName, price: initialServicePrice, quantity: 1, isProduct: false }
  ]);

  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'cartao_credito' | 'cartao_debito' | 'dinheiro'>('pix');
  const [copiedPix, setCopiedPix] = useState(false);

  const totalServices = comandaItems.filter(i => !i.canceled && !i.isProduct).reduce((acc, i) => acc + (i.price * i.quantity), 0);
  const totalProducts = comandaItems.filter(i => !i.canceled && i.isProduct).reduce((acc, i) => acc + (i.price * i.quantity), 0);
  const totalAmount = totalServices + totalProducts;

  const handleAddProduct = (prod: PosRetailProduct) => {
    setComandaItems(prev => {
      const existing = prev.find(i => i.id === prod.id);
      if (existing) {
        return prev.map(i => i.id === prod.id ? { ...i, quantity: i.quantity + 1, canceled: false } : i);
      }
      return [...prev, { id: prod.id, name: prod.name, price: prod.price, quantity: 1, isProduct: true }];
    });
  };

  const handleAddService = (serv: typeof QUICK_SERVICE_ADDONS[0]) => {
    setComandaItems(prev => {
      const existing = prev.find(i => i.id === serv.id);
      if (existing) {
        return prev.map(i => i.id === serv.id ? { ...i, quantity: i.quantity + 1, canceled: false } : i);
      }
      return [...prev, { id: serv.id, name: serv.name, price: serv.price, quantity: 1, isProduct: false }];
    });
  };

  const handleUpdateQuantity = (id: string, delta: number) => {
    setComandaItems(prev => {
      return prev.map(item => {
        if (item.id === id) {
          if (item.canceled) return item;
          const newQty = item.quantity + delta;
          return newQty > 0 ? { ...item, quantity: newQty } : null;
        }
        return item;
      }).filter(Boolean) as typeof comandaItems;
    });
  };

  // Alterna cancelamento lógico do item (marca como cancelado com risco visual ou restaura)
  const handleToggleCancelItem = (id: string) => {
    setComandaItems(prev => prev.map(i => i.id === id ? { ...i, canceled: !i.canceled } : i));
  };

  const handleRemoveItem = handleToggleCancelItem;

  const pixPayload = generateDynamicPixPayload({
    pixKey: providerPixKey,
    merchantName: providerName,
    merchantCity: providerCity,
    amount: totalAmount,
    txId: 'CMD' + Date.now().toString().slice(-6)
  });

  const handleCopyPix = () => {
    navigator.clipboard.writeText(pixPayload);
    setCopiedPix(true);
    setTimeout(() => setCopiedPix(false), 2500);
  };

  const handleFinalize = () => {
    if (onCompleteWithReceipt) {
      onCompleteWithReceipt({
        clientName,
        clientPhone,
        items: comandaItems.filter(i => !i.canceled).map(i => ({ name: i.name, price: i.price, quantity: i.quantity, isProduct: i.isProduct })),
        totalAmount,
        paymentMethod
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 overflow-y-auto animate-in fade-in">
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-zinc-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Cabeçalho */}
        <div className="bg-white border-b border-zinc-200 text-zinc-900 px-5 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="text-xl">💈</span>
            <div>
              <h2 className="text-sm font-black tracking-tight flex items-center gap-2 text-zinc-900">
                Comanda de Atendimento & Fechamento
                <span className="text-[10px] bg-amber-100 text-amber-900 border border-amber-200 font-black px-2 py-0.5 rounded-full">
                  + Produtos & Extras
                </span>
              </h2>
              <p className="text-[11px] text-zinc-500 font-medium">
                Cliente: <strong className="text-zinc-900">{clientName}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 p-1.5 rounded-xl hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Corpo Scrollável */}
        <div className="p-4 overflow-y-auto space-y-4 text-xs flex-1">
          
          {/* Seção 1: Itens da Comanda Atual */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-3.5 space-y-2">
            <div className="flex items-center justify-between text-zinc-600 font-bold border-b border-zinc-200/80 pb-2">
              <span className="flex items-center gap-1.5 text-zinc-900 font-black">
                <Scissors className="h-3.5 w-3.5 text-brand" /> Itens Consumidos / Realizados
              </span>
              <span className="text-[11px] text-zinc-500 font-medium">
                {comandaItems.length} item(ns)
              </span>
            </div>

            {comandaItems.length === 0 ? (
              <p className="text-center text-zinc-400 py-3 italic">Nenhum item lançado na comanda.</p>
            ) : (
              <div className="space-y-2 pt-1">
                {comandaItems.map((item) => {
                  const isCanceled = !!item.canceled;
                  return (
                    <div 
                      key={item.id} 
                      className={`flex items-center justify-between p-2.5 rounded-xl border text-xs transition-colors ${
                        isCanceled
                          ? 'bg-rose-50/40 border-rose-200/60'
                          : 'bg-white border-zinc-200 shadow-2xs'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs">{item.isProduct ? '🧴' : '✂️'}</span>
                        <div className="min-w-0">
                          <div className={`font-bold leading-tight truncate flex items-center gap-1.5 ${isCanceled ? 'line-through text-rose-700/80' : 'text-zinc-900'}`}>
                            <span className="truncate">{item.name}</span>
                            {isCanceled && (
                              <span className="text-[9px] font-bold text-rose-700 bg-rose-100 border border-rose-200/80 px-1 py-0.2 rounded uppercase shrink-0 no-underline inline-block">
                                Cancelado
                              </span>
                            )}
                          </div>
                          <p className={`text-[10px] ${isCanceled ? 'line-through text-rose-400' : 'text-zinc-500'}`}>
                            {formatCurrencyBRL(item.price)} cada
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {!isCanceled && (
                          <div className="flex items-center border border-zinc-200 rounded-lg bg-zinc-50">
                            <button
                              type="button"
                              onClick={() => handleUpdateQuantity(item.id, -1)}
                              className="px-2 py-0.5 text-zinc-600 hover:text-red-600 font-bold cursor-pointer"
                            >
                              -
                            </button>
                            <span className="px-2 font-black text-zinc-900">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => handleUpdateQuantity(item.id, 1)}
                              className="px-2 py-0.5 text-zinc-600 hover:text-emerald-600 font-bold cursor-pointer"
                            >
                              +
                            </button>
                          </div>
                        )}
                        <span className={`font-black min-w-[65px] text-right ${isCanceled ? 'line-through text-zinc-400' : 'text-zinc-900'}`}>
                          {formatCurrencyBRL(item.price * item.quantity)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleToggleCancelItem(item.id)}
                          className={`p-1 rounded cursor-pointer transition-colors ${
                            isCanceled
                              ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                              : 'text-zinc-400 hover:text-red-500 hover:bg-red-50'
                          }`}
                          title={isCanceled ? "Restaurar item na comanda" : "Cancelar item"}
                        >
                          {isCanceled ? <RotateCcw className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Seção 2: Adicionar Serviços Adicionais (1 Toque) */}
          <div>
            <p className="text-[11px] font-black text-zinc-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <span>✂️</span> Adicionais de Cadeira (Up-selling)
            </p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_SERVICE_ADDONS.map(addon => (
                <button
                  key={addon.id}
                  type="button"
                  onClick={() => handleAddService(addon)}
                  className="p-2 bg-white border border-zinc-200 hover:border-brand/40 hover:bg-brand/5 rounded-xl text-left transition-all flex items-center justify-between group cursor-pointer"
                >
                  <div className="truncate">
                    <p className="font-bold text-zinc-800 group-hover:text-brand truncate text-[11px]">
                      {addon.icon} {addon.name}
                    </p>
                    <p className="text-[10px] text-emerald-600 font-black">+{formatCurrencyBRL(addon.price)}</p>
                  </div>
                  <Plus className="h-3.5 w-3.5 text-zinc-400 group-hover:text-brand shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* Seção 3: Produtos de Balcão & Bebidas (Cross-selling) */}
          <div>
            <p className="text-[11px] font-black text-zinc-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <span>🧴</span> Produtos de Balcão & Bebidas (Cross-Selling)
            </p>
            <div className="grid grid-cols-3 gap-2">
              {DEFAULT_POS_PRODUCTS.map(prod => (
                <button
                  key={prod.id}
                  type="button"
                  onClick={() => handleAddProduct(prod)}
                  className="p-2 bg-white border border-zinc-200 hover:border-amber-500/40 hover:bg-amber-50/40 rounded-xl text-left transition-all flex flex-col justify-between group cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{prod.iconName}</span>
                    <span className="text-[9px] bg-zinc-100 text-zinc-500 font-bold px-1.5 py-0.5 rounded-md">
                      Estq: {prod.stockQuantity}
                    </span>
                  </div>
                  <p className="font-bold text-zinc-900 text-[10px] mt-1 leading-tight line-clamp-1">
                    {prod.name}
                  </p>
                  <p className="text-[11px] font-black text-amber-700 mt-0.5">
                    {formatCurrencyBRL(prod.price)}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Seção 4: Seleção de Forma de Pagamento */}
          <div>
            <p className="text-[11px] font-black text-zinc-700 uppercase tracking-wider mb-1.5">
              Forma de Pagamento
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { key: 'pix', label: 'PIX QrCode', icon: '⚡' },
                { key: 'cartao_credito', label: 'Crédito', icon: '💳' },
                { key: 'cartao_debito', label: 'Débito', icon: '💳' },
                { key: 'dinheiro', label: 'Dinheiro', icon: '💵' }
              ].map(method => (
                <button
                  key={method.key}
                  type="button"
                  onClick={() => setPaymentMethod(method.key as any)}
                  className={'p-2 rounded-xl border text-center transition-all cursor-pointer ' + (
                    paymentMethod === method.key
                      ? 'bg-neutral-900 text-white border-neutral-900 shadow-xs'
                      : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'
                  )}
                >
                  <div className="text-sm">{method.icon}</div>
                  <div className="text-[10px] font-bold mt-0.5">{method.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* QR Code PIX Dinâmico Instantâneo */}
          {paymentMethod === 'pix' && (
            <div className="bg-emerald-50/70 border border-emerald-200 p-3 rounded-2xl flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-600 text-white rounded-xl">
                  <QrCode className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold text-emerald-950 text-xs">PIX Pronto para Pagamento</p>
                  <p className="text-[10px] text-emerald-800">
                    Chave: <strong>{providerPixKey}</strong> • Total: <strong>{formatCurrencyBRL(totalAmount)}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCopyPix}
                className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-black text-[11px] transition-all flex items-center gap-1 cursor-pointer"
              >
                {copiedPix ? <Check className="h-3.5 w-3.5" /> : <Smartphone className="h-3.5 w-3.5" />}
                {copiedPix ? 'Copiado!' : 'Copiar PIX'}
              </button>
            </div>
          )}

        </div>

        {/* Rodapé com Resumo Financeiro & Ações */}
        <div className="bg-zinc-100 p-4 border-t border-zinc-200 flex items-center justify-between gap-3 shrink-0">
          <div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block">
              Total a Pagar
            </span>
            <span className="text-xl font-black text-zinc-950 tracking-tight">
              {formatCurrencyBRL(totalAmount)}
            </span>
            {totalProducts > 0 && (
              <span className="text-[10px] text-zinc-500 block">
                (Serviços: {formatCurrencyBRL(totalServices)} + Balcão: {formatCurrencyBRL(totalProducts)})
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleFinalize}
              className="px-4 py-2.5 bg-brand hover:bg-brand-dark text-white rounded-2xl font-black text-xs transition-all shadow-md flex items-center gap-2 cursor-pointer active:scale-95"
            >
              <CheckCircle2 className="h-4 w-4" />
              Finalizar & Emitir Recibo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
